import { Link } from "react-router-dom";
import { ShieldCheck, BotMessageSquare, Radar, ArrowRight, type LucideIcon } from "lucide-react";

type PillarKey = "eqms" | "agents" | "hub";

interface PillarLink {
  key: PillarKey;
  icon: LucideIcon;
  label: string;
  href: string;
  blurb: string;
}

const ALL: PillarLink[] = [
  { key: "eqms", icon: ShieldCheck, label: "Pharmalify Spine", href: "/spine", blurb: "The unified GxP operations backbone: Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS)." },
  { key: "agents", icon: BotMessageSquare, label: "AI Agents inside Spine", href: "/ai-agents", blurb: "Native, governed, audit-trailed assistants that propose and cite — never decide or write records." },
  { key: "hub", icon: Radar, label: "Regulatory Watch", href: "/regulatory-hub", blurb: "Native regulatory intelligence feeding Spine’s Change Control, QRM and SOP lifecycle." },
];

interface Props {
  /** The pillar the visitor is currently on — it will be hidden from the band. */
  current: PillarKey;
}

/**
 * "Inside Pharmalify Spine" cross-link band.
 * Rendered at the bottom of each pillar page so visitors can hop to the other two.
 */
const SuiteCrossLink = ({ current }: Props) => {
  const others = ALL.filter((p) => p.key !== current);

  return (
    <section className="border-t border-border/60 bg-muted/40">
      <div className="container py-12 md:py-14 max-w-6xl">
        <div className="text-center mb-8">
          <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-accent mb-2 px-3 py-1 rounded-full bg-accent/10">
            Inside Pharmalify Spine
          </span>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
             One backbone, three pillars, native intelligence
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {others.map((p) => (
            <Link
              key={p.key}
              to={p.href}
              className="group flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-5 hover:border-accent/40 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                <p.icon className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
                  {p.label}
                  <ArrowRight className="w-4 h-4 text-accent opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.blurb}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SuiteCrossLink;
